// src/services/moduloService.js
import api from './api';

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

/**
 * Extrai ids a partir de userInfo (vindo do AuthContext) ou localStorage.
 */
const getIds = (userInfo) => {
  if (userInfo) {
    return {
      usuarioId: userInfo.id ?? null,
      estudanteId: userInfo.estudanteId ?? null,
      userTipo: userInfo.tipo ?? null
    };
  }

  const stored = JSON.parse(localStorage.getItem('usuario')) || {};
  return {
    usuarioId: stored.id ?? null,
    estudanteId: stored.estudanteId ?? null,
    userTipo: stored.tipo ?? null
  };
};

/**
 * Verifica se o usuário tem acesso privilegiado aos módulos TCC
 * Inclui supervisor e presidente
 */
const hasPrivilegedTCCAccess = (codigo, userTipo) => {
  const modulosTCC = [
    'TCC_MONOGRAFIAS', 
    'TCC_PROJETOS', 
    'TCC_PROPOSTAS', 
    'TCC_DEFESAS',
    'TCC_AVALIACOES'
  ];
  
  const privilegedRoles = ['supervisor', 'presidente'];
  
  return privilegedRoles.includes(userTipo) && modulosTCC.includes(codigo);
};

/**
 * Verifica se o utilizador tem acesso automático (sem API call) aos módulos TCC.
 * Todos os papéis do fluxo TCC têm acesso — a activação do módulo é gerida pela
 * instituição, não individualmente por papel.
 */
const isSupervisorTCCModule = (codigo, userTipo) => {
  const modulosTCC = [
    'TCC_MONOGRAFIAS',
    'TCC_PROJETOS',
    'TCC_PROPOSTAS',
    'TCC_DEFESAS',
    'TCC_AVALIACOES'
  ];

  const rolesTCC = [
    'supervisor', 'presidente', 'arguente',
    'estudante', 'chefe_departamento', 'revisor_projeto',
    'revisor', 'direcao_cientifica'
  ];

  return rolesTCC.includes(userTipo) && modulosTCC.includes(codigo);
};

/**
 * Verifica se o usuário é admin e tem acesso total
 */
const isAdminUser = (userTipo) => {
  return userTipo === 'admin' || userTipo === 'instituicao';
};

/* ------------------------------------------------------------------ */
/*  Operações principais                                              */
/* ------------------------------------------------------------------ */

const listarModulos = async () => {
  const { data } = await api.get('/api/modulos');
  return data;
};

const obterModulo = async (id) =>
  (await api.get(`/api/modulos/${id}`)).data;

const listarModulosPorInstituicao = async (instituicaoId) =>
  (await api.get(`/api/modulos/instituicao/${instituicaoId}`)).data;

/**
 * Verifica se o utilizador tem acesso ao módulo <codigo>.
 *
 * @param {string}  codigo               – Ex.: 'TCC_MONOGRAFIAS'
 * @param {object}  [userInfo]           – Objeto user vindo do AuthContext
 * @param {boolean} [forceRefresh=false] – Caso precise forçar refresh-token
 */
const verificarAcessoModulo = async (
  codigo,
  userInfo = null,
  forceRefresh = false
) => {
  try {
    console.log(`[moduloService] → verificarAcessoModulo ${codigo}`);

    const { usuarioId, estudanteId, userTipo } = getIds(userInfo);

    if (!usuarioId && !estudanteId) {
      console.warn('[moduloService] nenhum usuário identificado');
      return false;
    }

    // ============================================
    // VERIFICAÇÃO ESPECIAL PARA SUPERVISORES E PRESIDENTES
    // ============================================
    if (isSupervisorTCCModule(codigo, userTipo)) {
      console.log(`[moduloService] → Acesso automático para ${userTipo} no módulo ${codigo}`);
      return true;
    }

    // ============================================
    // VERIFICAÇÃO ESPECIAL PARA ADMINISTRADORES
    // ============================================
    if (isAdminUser(userTipo)) {
      console.log(`[moduloService] → Acesso automático para admin no módulo ${codigo}`);
      return true;
    }

    // Opcional: força refresh de token se o seu backend suportar
    if (forceRefresh) {
      try {
        await api.post('/auth/refresh-token');
      } catch {
        /* ignora se a rota não existir */
      }
    }

    /* 1️⃣ — rota pública */
    const base = estudanteId
      ? `estudanteId=${estudanteId}`
      : `usuarioId=${usuarioId}`;
    const urlPublic = `/estudante-modulo-acesso?${base}&modulo=${codigo}`;
    
    try {
      const { data: pub } = await api.get(urlPublic);
      console.log('→ resposta pública', pub);
      if (pub?.access) return true;
    } catch (publicError) {
      console.warn('[moduloService] Erro na verificação pública, tentando rota autenticada:', publicError.response?.data);
      
      // Se é supervisor/presidente e a rota pública falhou, tentar rota autenticada
      if (isSupervisorTCCModule(codigo, userTipo)) {
        console.log(`[moduloService] → Tentando rota autenticada para ${userTipo}`);
      }
    }

    /* 2️⃣ — fallback autenticado */
    try {
      const { data: auth } = await api.get(`/modulos/verificar-acesso/${codigo}`);
      console.log('→ resposta autenticada', auth);
      return !!auth?.access;
    } catch (authError) {
      console.warn('[moduloService] Erro na verificação autenticada:', authError.response?.data);
      
      // ============================================
      // FALLBACK FINAL PARA SUPERVISORES E PRESIDENTES
      // ============================================
      if (isSupervisorTCCModule(codigo, userTipo)) {
        console.log(`[moduloService] → Fallback: liberando acesso para ${userTipo} no módulo ${codigo}`);
        return true;
      }
      
      return false;
    }
  } catch (err) {
    console.error('[moduloService] erro verificarAcessoModulo:', err);
    
    // ============================================
    // ÚLTIMO RECURSO PARA SUPERVISORES E PRESIDENTES
    // ============================================
    const { userTipo } = getIds(userInfo);
    if (isSupervisorTCCModule(codigo, userTipo)) {
      console.log(`[moduloService] → Último recurso: liberando acesso para ${userTipo} no módulo ${codigo}`);
      return true;
    }
    
    return false;
  }
};

/**
 * Versão alternativa que verifica localmente primeiro para supervisores e presidentes
 */
const verificarAcessoModuloLocal = (codigo, userInfo = null) => {
  console.log(`[moduloService] → verificarAcessoModuloLocal ${codigo}`);
  
  const { usuarioId, estudanteId, userTipo } = getIds(userInfo);

  if (!usuarioId && !estudanteId) {
    console.warn('[moduloService] nenhum usuário identificado');
    return false;
  }

  // Verificação local para supervisores e presidentes
  if (isSupervisorTCCModule(codigo, userTipo)) {
    console.log(`[moduloService] → Acesso local liberado para ${userTipo} no módulo ${codigo}`);
    return true;
  }

  // Verificação local para admins
  if (isAdminUser(userTipo)) {
    console.log(`[moduloService] → Acesso local liberado para admin no módulo ${codigo}`);
    return true;
  }

  // Para outros casos, retornar null para indicar que precisa verificar no servidor
  return null;
};

/**
 * Activa / desactiva módulo para uma instituição.
 */
const gerenciarModuloInstituicao = async (instituicaoId, moduloId, ativo) =>
  (
    await api.post(
      `/api/modulos/instituicao/${instituicaoId}/modulo/${moduloId}`,
      { ativo }
    )
  ).data;

/* ------------------------------------------------------------------ */
/*  Export default                                                    */
/* ------------------------------------------------------------------ */

export default {
  listarModulos,
  obterModulo,
  listarModulosPorInstituicao,
  verificarAcessoModulo,
  verificarAcessoModuloLocal,
  gerenciarModuloInstituicao,
  // Exportar helpers para uso em outros lugares se necessário
  isSupervisorTCCModule,
  isAdminUser,
  hasPrivilegedTCCAccess // Nova função adicional
};